import {
  EntityType,
  useGetQuestionsByIdAndQuestionCreationTaskIdQuery,
  WorkflowStepCode,
} from '@exam-management/graphql';
import { type Path, useNavigate } from '@exam-management/router';
import { useAuth } from '@shared/auth';
import {
  type DataGridProps,
  type SxPropsWithTheme,
  DataGrid,
  ErrorState,
  mergeSx,
  Flex,
} from '@shared/components';
import { useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { EmptyQuestionList, QuestionTypeLabel } from '../../components';
import { useQuestionCreationTaskDetails } from '../../providers';
import { transformQuestionsToQuestionRows } from '../../utils';

import { getColumns } from './columns';

const defaultSx: SxPropsWithTheme = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.rounded(0),
  '--unstable_DataGrid-radius': 0,
  border: `0`,
  boxShadow: 'none',
  '.MuiDataGrid-cell': {
    padding: theme.spacing(3),
  },
  '.MuiDataGrid-cell[data-field="difficulty"], .MuiDataGrid-cell[data-field="calculation"]': {
    padding: theme.spacing(1.5, 2),
  },
  '.MuiDataGrid-cell[data-field="moreAction"]': {
    padding: theme.spacing(1.5),
  },
  '.MuiDataGrid-columnHeader': {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[100],
  },
  '.MuiDataGrid-columnHeaders': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
    borderColor: theme.palette.divider,
  },
  '.MuiDataGrid-cell:last-child': {
    padding: theme.spacing(1.5),
  },
  '.MuiDataGrid-cellCheckbox': {
    padding: theme.spacing(0),
  },
  '--DataGrid-overlayHeight': '124px',
});

type SingleQuestionListProps = Omit<DataGridProps, 'rows' | 'columns'>;

export default function SingleQuestionList(props: Readonly<SingleQuestionListProps>) {
  const { userProfile } = useAuth();
  const { task } = useQuestionCreationTaskDetails();

  const navigate = useNavigate();
  const location = useLocation();
  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);

  const getQuestionPath = useCallback((): Path => {
    if (matchedTaskManagement) {
      return `/task-management/question-creation/:taskId/single/:questionId`;
    }
    return `/my-task/question-creation/:taskId/single/:questionId`;
  }, [matchedTaskManagement]);

  const {
    data,
    loading: isLoading,
    error,
  } = useGetQuestionsByIdAndQuestionCreationTaskIdQuery({
    variables: {
      ids: (task?.taskEntities ?? [])
        .filter(({ entityType }) => entityType === EntityType.Question)
        .map(({ entityId }) => entityId),
      taskId: task.id,
    },
  });

  const shouldDisplayMoreAction =
    task.currentWorkflowStep.code === WorkflowStepCode.QuestionCreationInProgress &&
    task.assignee.id === userProfile?.id;
  const columns = useMemo(
    () => getColumns({ hideMoreAction: !shouldDisplayMoreAction }),
    [shouldDisplayMoreAction],
  );

  const rows = useMemo(
    () =>
      transformQuestionsToQuestionRows(data?.qctGetTaskQuestionsByIds).map((row) => ({
        ...row,
        questionUrl: {
          path: getQuestionPath(),
          params: {
            taskId: task.id,
            questionId: row.id,
          },
        },
      })),
    [data, getQuestionPath, task.id],
  );

  if (error) {
    return <ErrorState />;
  }

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        borderRadius: theme.shape.rounded(2),
        border: `1px solid ${theme.palette.grey[200]}`,
        overflow: 'hidden',
      })}
    >
      <QuestionTypeLabel type='single' />
      <DataGrid
        rows={rows}
        columns={columns}
        getRowHeight={() => 'auto'}
        loading={isLoading}
        disableRowSelectionOnClick
        sx={mergeSx(defaultSx, props.sx)}
        disableColumnMenu
        hideFooter
        slots={{
          noRowsOverlay: () => <EmptyQuestionList title='ยังไม่มีคำถาม Single' />,
        }}
        onRowClick={(params) =>
          navigate(
            matchedTaskManagement
              ? `/task-management/question-creation/:taskId/single/:questionId`
              : `/my-task/question-creation/:taskId/single/:questionId`,
            {
              params: {
                taskId: task.id,
                questionId: params.row.id,
              },
            },
          )
        }
        {...props}
      />
    </Flex>
  );
}
